import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { TimeseriesPreviewCard } from '../../components/admin/TimeseriesPreviewCard';
import { LoanCashflowUploadCard } from '../../components/admin/LoanCashflowUploadCard';
import { FirstDrawReviewCard } from '../../components/admin/FirstDrawReviewCard';
import { 
  Upload, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Database,
  Users,
  Building2,
  RefreshCw,
  Download,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDataImportPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // Loan book state
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [clearExisting, setClearExisting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Investor/client upload state
  const [investorFile, setInvestorFile] = useState(null);
  const [investorUploading, setInvestorUploading] = useState(false);
  const [investorResult, setInvestorResult] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [clientFile, setClientFile] = useState(null);
  const [clientUploading, setClientUploading] = useState(false);
  const [clientResult, setClientResult] = useState(null);

  // LoanBook engine (shadow mode) toggle
  const [engineStatus, setEngineStatus] = useState(null);
  const [engineLoading, setEngineLoading] = useState(true);
  const [engineToggling, setEngineToggling] = useState(false);

  useEffect(() => { fetchStatus(); fetchEngineStatus(); }, [token]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/import/status`, { headers });
      setStatus(response.data);
    } catch (err) {
      toast.error('Failed to load import status');
    } finally {
      setLoading(false);
    }
  };

  const fetchEngineStatus = async () => {
    try {
      setEngineLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/loanbook-engine/status`, { headers });
      setEngineStatus(response.data);
    } catch (err) {
      toast.error('Failed to load LoanBook Engine status');
    } finally {
      setEngineLoading(false);
    }
  };

  const [exportingLoanbook, setExportingLoanbook] = useState(false);

  const handleExportLoanbookXlsx = async () => {
    setExportingLoanbook(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/loanbook/export.xlsx`, { headers, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loanbook-rp-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export LoanBook/RP workbook');
    } finally {
      setExportingLoanbook(false);
    }
  };

  const handleEngineToggle = async (enabled) => {
    setEngineToggling(true);
    try {
      await axios.post(`${API_URL}/api/admin/loanbook-engine/toggle`, { enabled }, { headers });
      toast.success(enabled ? 'LoanBook Engine shadow job enabled' : 'LoanBook Engine shadow job disabled');
      await fetchEngineStatus();
    } catch (err) {
      toast.error('Failed to update LoanBook Engine toggle');
    } finally {
      setEngineToggling(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.xlsb')) {
        toast.error('Please select an Excel file (.xlsx, .xls, or .xlsb)');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Please select a file first'); return; }
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      setUploading(true);
      setImportResult(null);
      setUploadProgress(0);
      const response = await axios.post(
        `${API_URL}/api/admin/import/loanbook/upload?clear_existing=${clearExisting}`,
        formData,
        { 
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }, 
          timeout: 600000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || progressEvent.loaded));
            setUploadProgress(pct);
          }
        }
      );
      
      setUploadProgress(100);
      const jobId = response.data.job_id;
      if (jobId) {
        // Async import — poll for completion
        toast.info('File uploaded — processing your data...');
        const pollInterval = setInterval(async () => {
          try {
            const status = await axios.get(`${API_URL}/api/admin/import/loanbook/status/${jobId}`, { headers });
            if (status.data.status === 'completed') {
              clearInterval(pollInterval);
              setImportResult(status.data.results);
              const r = status.data.results;
              const total = (r.loan_book_imported || 0) + (r.loan_book_updated || 0) + (r.risk_participation_imported || 0) + (r.risk_participation_updated || 0);
              toast.success(`Import completed — ${total.toLocaleString()} records processed`);
              setUploading(false);
              setUploadProgress(0);
              fetchStatus();
            } else if (status.data.status === 'failed') {
              clearInterval(pollInterval);
              toast.error(`Import failed: ${status.data.error || 'Unknown error'}`);
              setUploading(false);
              setUploadProgress(0);
            }
          } catch (pollErr) {
            // Poll error, keep trying
          }
        }, 3000);
        // Safety timeout after 10 minutes
        setTimeout(() => { clearInterval(pollInterval); setUploading(false); setUploadProgress(0); }, 600000);
      } else {
        // Sync response (backward compat)
        setImportResult(response.data.results);
        toast.success('Import completed successfully');
        fetchStatus();
        setUploading(false);
        setUploadProgress(0);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg = 'Upload failed';
      if (status === 413) {
        msg = 'File too large — check server upload limit (nginx client_max_body_size)';
      } else if (status === 422) {
        msg = detail || 'Invalid file format';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'Upload timed out — file may be too large or connection is slow';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        // Common causes: (a) the file exceeds nginx client_max_body_size and
        // the proxy severs the connection; (b) an intermediate reverse-proxy
        // (Cloudflare / nginx-proxy) hit proxy_read_timeout during upload; or
        // (c) the browser is offline.  Give the admin enough hints to
        // triage — the raw axios error is logged to the console below.
        const sizeMB = (selectedFile?.size / (1024 * 1024)).toFixed(1);
        msg = `Network error — upload never reached the server. File size: ${sizeMB}MB. ` +
              `Likely: nginx client_max_body_size / proxy_read_timeout hit, or the proxy ` +
              `container was rebuilt without the ingest cluster restart. ` +
              `Check devtools → Network → the failed request for details.`;
      } else if (detail) {
        msg = detail;
      }
      toast.error(msg);
      console.error('Upload error:', { status, detail, code: err.code, message: err.message });
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const downloadTemplate = (type) => {
    const url = `${API_URL}/api/admin/bulk/${type}-template?v=${Date.now()}`;
    fetch(url, { headers })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${type}_upload_template.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      }).catch(() => toast.error('Download failed'));
  };

  const handleInvestorUpload = async (forceApply = false) => {
    if (!investorFile) { toast.error('Please select a file'); return; }
    setInvestorUploading(true);
    setEmailResult(null);
    try {
      const fd = new FormData();
      fd.append('file', investorFile);
      const mode = forceApply ? 'apply' : 'preview';
      const r = await axios.post(`${API_URL}/api/admin/bulk/upload-investors?mode=${mode}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setInvestorResult(r.data);
      if (r.data.mode === 'preview' && r.data.conflict_count > 0) {
        toast.warning(`${r.data.conflict_count} conflict(s) found. Review and confirm to merge.`);
      } else if (r.data.mode === 'preview') {
        // No conflicts — auto-apply
        fd.append('file', investorFile);
        const applyR = await axios.post(`${API_URL}/api/admin/bulk/upload-investors?mode=apply`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        setInvestorResult(applyR.data);
        toast.success(applyR.data.message);
      } else {
        toast.success(r.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setInvestorUploading(false);
    }
  };

  const handleConfirmMerge = async () => {
    if (!investorFile) return;
    await handleInvestorUpload(true);
  };

  const handleSendWelcomeEmails = async () => {
    if (!investorResult?.created_user_ids?.length) return;
    setSendingEmails(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/bulk/send-welcome-emails`,
        { user_ids: investorResult.created_user_ids },
        { headers }
      );
      setEmailResult(r.data);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleClientUpload = async () => {
    if (!clientFile) { toast.error('Please select a file'); return; }
    setClientUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', clientFile);
      const r = await axios.post(`${API_URL}/api/admin/bulk/upload-clients`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setClientResult(r.data);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setClientUploading(false);
    }
  };

  const UploadResult = ({ result, type }) => (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2 text-sm">
      <p className="font-medium text-blue-800">{result.message}</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-2 bg-green-100 rounded">
          <p className="text-green-700 font-bold text-lg">{result.created_count}</p>
          <p className="text-green-600 text-xs">Created</p>
        </div>
        <div className="text-center p-2 bg-amber-100 rounded">
          <p className="text-amber-700 font-bold text-lg">{result.updated_count}</p>
          <p className="text-amber-600 text-xs">Updated</p>
        </div>
        <div className="text-center p-2 bg-red-100 rounded">
          <p className="text-red-700 font-bold text-lg">{result.error_count}</p>
          <p className="text-red-600 text-xs">Errors</p>
        </div>
      </div>
      {result.created?.length > 0 && (
        <details className="mt-2" open>
          <summary className="cursor-pointer text-green-700 font-medium text-xs">
            View {result.created.length} newly created {type}{result.created.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-1 p-3 bg-green-50 rounded border border-green-200 max-h-48 overflow-y-auto">
            {result.created.map((c, i) => (
              <p key={i} className="text-green-800 text-xs">{c}</p>
            ))}
          </div>
        </details>
      )}
      {result.updated?.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-amber-700 font-medium text-xs">
            View {result.updated.length} updated (matched existing) {type}{result.updated.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-1 p-3 bg-amber-50 rounded border border-amber-200 max-h-48 overflow-y-auto">
            {result.updated.map((u, i) => (
              <p key={i} className="text-amber-800 text-xs">{u}</p>
            ))}
          </div>
        </details>
      )}
      {result.errors?.length > 0 && (
        <div className="mt-2 p-3 bg-red-50 rounded border border-red-200 max-h-40 overflow-y-auto">
          {result.errors.map((e, i) => (
            <p key={i} className="text-red-700 text-xs">Row {e.row}: {e.error}</p>
          ))}
        </div>
      )}
    </div>
  );

  // Diagnostic state
  const [diagnostic, setDiagnostic] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const fetchDiagnostic = async () => {
    setLoadingDiag(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/import/diagnostic`, { headers });
      setDiagnostic(r.data);
    } catch (e) {
      toast.error('Diagnostic failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingDiag(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-import-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Data Import</h1>
          <p className="text-[#6C757D]">Import loan book, investor, and client data</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDiagnostic} disabled={loadingDiag} data-testid="diag-btn">
            {loadingDiag ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AlertCircle className="h-4 w-4 mr-2" />}
            Diagnose
          </Button>
          <Button variant="outline" onClick={fetchStatus} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Status
          </Button>
        </div>
      </div>

      {/* Diagnostic panel — shows the actual DB state after an import.
          Use this whenever counts on other pages don't match expectations. */}
      {diagnostic && (
        <Card data-testid="diagnostic-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              Import Diagnostic
            </CardTitle>
            <CardDescription>Live counts of every critical collection + last import job result.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              {Object.entries(diagnostic.counts || {}).map(([k, v]) => (
                <div key={k} className={`p-2 rounded border ${v > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="text-xs text-gray-600 font-mono">{k}</p>
                  <p className="text-lg font-bold">{v?.toLocaleString?.() ?? v}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded border">
                <p className="text-xs font-semibold mb-1">expected_cashflows.source_sheet distinct</p>
                <p className="text-sm font-mono">{JSON.stringify(diagnostic.distinct_keys?.['expected_cashflows.source_sheet'] || [])}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded border">
                <p className="text-xs font-semibold mb-1">expected_cashflows.kind distinct</p>
                <p className="text-sm font-mono">{JSON.stringify(diagnostic.distinct_keys?.['expected_cashflows.kind'] || [])}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded border md:col-span-2">
                <p className="text-xs font-semibold mb-1">risk_participation.investor_id (first 30 non-empty)</p>
                <p className="text-xs font-mono break-all">
                  {(diagnostic.distinct_keys?.['risk_participation.investor_id'] || []).join(', ') || '(EMPTY — recon dropdown will be empty)'}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded border md:col-span-2">
                <p className="text-xs font-semibold mb-1">expected_cashflows.owner_code (first 30 non-empty)</p>
                <p className="text-xs font-mono break-all">
                  {(diagnostic.distinct_keys?.['expected_cashflows.owner_code'] || []).join(', ') || '(EMPTY)'}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded border md:col-span-2">
                <p className="text-xs font-semibold mb-1">investor_transactions.investor_id (first 30 non-empty) — Recon dropdown source</p>
                <p className="text-xs font-mono break-all">
                  {(diagnostic.distinct_keys?.['investor_transactions.investor_id'] || []).join(', ') || '(EMPTY — recon dropdown WILL be empty)'}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded border md:col-span-2">
                <p className="text-xs font-semibold mb-1">users.risk_participant_code where role=investor</p>
                <p className="text-xs font-mono break-all">
                  {(diagnostic.distinct_keys?.['users.risk_participant_code (role=investor)'] || []).join(', ') || '(EMPTY — all investors will show R0 portfolio)'}
                </p>
              </div>
            </div>

            {/* Users ↔ risk_participation JOIN diagnostic — this is the crown jewel:
                it tells you exactly why Investors page shows R0 for everyone. */}
            {diagnostic.users_rp_join && (
              <div className={`p-3 rounded border ${diagnostic.users_rp_join.intersection_count === 0 ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
                <p className="text-xs font-semibold mb-2">
                  users.risk_participant_code ↔ risk_participation.risk_participant JOIN
                </p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="font-medium">Overlap</p>
                    <p className="font-mono text-lg">{diagnostic.users_rp_join.intersection_count}</p>
                    <p className="text-gray-600 break-all">
                      {(diagnostic.users_rp_join.intersection_first_30 || []).slice(0, 8).join(', ') || '(none)'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-red-700">Users with no RP match</p>
                    <p className="font-mono text-lg text-red-700">{(diagnostic.users_rp_join.users_without_matching_rp_first_30 || []).length}+</p>
                    <p className="text-gray-600 break-all">
                      {(diagnostic.users_rp_join.users_without_matching_rp_first_30 || []).slice(0, 8).join(', ') || '(none)'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-amber-700">RP codes with no user</p>
                    <p className="font-mono text-lg text-amber-700">{(diagnostic.users_rp_join.rp_without_matching_user_first_30 || []).length}+</p>
                    <p className="text-gray-600 break-all">
                      {(diagnostic.users_rp_join.rp_without_matching_user_first_30 || []).slice(0, 8).join(', ') || '(none)'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2 italic">{diagnostic.users_rp_join.note}</p>
              </div>
            )}
            {/* Live portfolio sample — mirrors what the Investors page shows.
                If these values are non-zero here but R0 on the Investors page,
                it's a frontend caching / build issue, NOT a backend/data bug. */}
            {diagnostic.live_portfolio_sample?.length > 0 && (
              <div className="p-3 rounded border bg-emerald-50 border-emerald-300">
                <p className="text-xs font-semibold mb-2">
                  Live portfolio sample — first 5 investors (mirrors what GET /api/admin/investors returns)
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left">
                      <th className="p-1">Email</th>
                      <th className="p-1">rp_code</th>
                      <th className="p-1 text-right">active_loans</th>
                      <th className="p-1 text-right">portfolio_value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostic.live_portfolio_sample.map((s, i) => (
                      <tr key={i} className={s.portfolio_value === 0 ? 'bg-red-50' : ''}>
                        <td className="p-1 font-mono">{s.email || '(none)'}</td>
                        <td className="p-1 font-mono">{s.rp_code}</td>
                        <td className="p-1 text-right font-mono">{s.active_loans_count}</td>
                        <td className="p-1 text-right font-mono">R {Number(s.portfolio_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-600 mt-2 italic">
                  If portfolio_value is non-zero here but the Investors page still shows R0,
                  it&apos;s a frontend cache issue — try hard-refresh (Ctrl+Shift+R) or open in an incognito window.
                </p>
              </div>
            )}

            {diagnostic.last_import_job && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs font-semibold mb-1">Last import job — status: <Badge>{diagnostic.last_import_job.status}</Badge></p>
                <pre className="text-xs bg-white p-2 rounded max-h-64 overflow-auto">
                  {JSON.stringify({
                    filename: diagnostic.last_import_job.filename,
                    status: diagnostic.last_import_job.status,
                    error: diagnostic.last_import_job.error,
                    started_at: diagnostic.last_import_job.started_at,
                    completed_at: diagnostic.last_import_job.completed_at,
                    sheets_found: diagnostic.last_import_job.results?.sheets_found,
                    sheets_resolved: diagnostic.last_import_job.results?.sheets_resolved,
                    loan_book_imported: diagnostic.last_import_job.results?.loan_book_imported,
                    loan_book_updated: diagnostic.last_import_job.results?.loan_book_updated,
                    risk_participation_imported: diagnostic.last_import_job.results?.risk_participation_imported,
                    risk_participation_updated: diagnostic.last_import_job.results?.risk_participation_updated,
                    investors_identified: diagnostic.last_import_job.results?.investors_identified,
                    errors: (diagnostic.last_import_job.results?.errors || []).slice(0, 20),
                    warnings: (diagnostic.last_import_job.results?.warnings || []).slice(0, 20),
                  }, null, 2)}
                </pre>
              </div>
            )}

            {/* Recent jobs list + heartbeat — lets admin see if a "processing"
                job is genuinely alive (elapsed growing, heartbeat.import_ids
                changing between clicks) or dead (stuck for hours). Killing
                a stuck job removes the "processing" limbo without touching data. */}
            {diagnostic.recent_import_jobs?.length > 0 && (
              <div className="p-3 rounded border bg-gray-50">
                <p className="text-xs font-semibold mb-2">Recent import jobs (last 5)</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="p-1">Filename</th>
                      <th className="p-1">Status</th>
                      <th className="p-1 text-right">Elapsed</th>
                      <th className="p-1">Started</th>
                      <th className="p-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostic.recent_import_jobs.map((j) => (
                      <tr key={j.id} className={j.likely_stuck ? 'bg-red-50' : ''}>
                        <td className="p-1 font-mono truncate max-w-xs">{j.filename}</td>
                        <td className="p-1">
                          <Badge variant={j.status === 'failed' ? 'destructive' : j.status === 'completed' ? 'default' : 'secondary'}>
                            {j.status}
                          </Badge>
                          {j.likely_stuck && <span className="ml-1 text-red-700 text-xs">STUCK?</span>}
                        </td>
                        <td className="p-1 text-right font-mono">
                          {j.elapsed_seconds != null
                            ? (j.elapsed_seconds >= 60
                                ? `${Math.floor(j.elapsed_seconds / 60)}m ${Math.floor(j.elapsed_seconds % 60)}s`
                                : `${j.elapsed_seconds}s`)
                            : '-'}
                        </td>
                        <td className="p-1 text-xs">{(j.started_at || '').replace('T', ' ').slice(0, 19)}</td>
                        <td className="p-1">
                          {j.status === 'processing' && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm(`Kill job ${j.id}?\n\nThis marks it 'failed' without rolling back partial writes.`)) return;
                                try {
                                  await axios.post(`${API_URL}/api/admin/import/jobs/${j.id}/kill`, {}, { headers });
                                  toast.success('Job marked failed');
                                  fetchDiagnostic();
                                } catch (e) {
                                  toast.error('Kill failed: ' + (e.response?.data?.detail || e.message));
                                }
                              }}
                              data-testid={`kill-job-${j.id}`}
                            >
                              Kill
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Heartbeat block — click Diagnose again in 60s and compare
                    these values. Any change = the "processing" job is alive. */}
                {diagnostic.heartbeat && (
                  <div className="mt-3 pt-2 border-t text-xs">
                    <p className="font-medium mb-1">
                      Heartbeat @ server time <span className="font-mono">{diagnostic.heartbeat.server_time_utc?.slice(0, 19)}</span>
                    </p>
                    <p className="text-gray-600 italic mb-1">
                      Click Diagnose again in 60s and compare — if any of these change, the running import IS writing.
                    </p>
                    <pre className="text-xs bg-white p-2 rounded max-h-32 overflow-auto">
                      {JSON.stringify(diagnostic.heartbeat.latest_import_id_seen, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Database Status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#6C757D]">Loan Book Entries</p><p className="text-2xl font-bold">{loading ? '-' : status?.loan_book_entries?.toLocaleString()}</p></div><div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center"><Database className="h-5 w-5 text-blue-600" /></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#6C757D]">Risk Participation</p><p className="text-2xl font-bold">{loading ? '-' : status?.risk_participation_entries?.toLocaleString()}</p></div><div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center"><FileText className="h-5 w-5 text-green-600" /></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#6C757D]">Investors</p><p className="text-2xl font-bold">{loading ? '-' : status?.unique_investors}</p></div><div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center"><Users className="h-5 w-5 text-purple-600" /></div></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-[#6C757D]">Client Profiles</p><p className="text-2xl font-bold">{loading ? '-' : status?.client_profiles}</p></div><div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center"><Building2 className="h-5 w-5 text-amber-600" /></div></div></CardContent></Card>
      </div>

      <Tabs defaultValue="loanbook" data-testid="import-tabs">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto w-full max-w-2xl">
          <TabsTrigger value="loanbook" data-testid="tab-loanbook">Loan Book</TabsTrigger>
          <TabsTrigger value="investors" data-testid="tab-investors">Investor Profiles</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Client Profiles</TabsTrigger>
          <TabsTrigger value="loan-cashflows" data-testid="tab-loan-cashflows">Loan Cashflows</TabsTrigger>
        </TabsList>

        {/* ===== LOAN BOOK TAB ===== */}
        <TabsContent value="loanbook" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" />Upload Loan Book</CardTitle>
              <CardDescription>Excel file with LB (Loan Book) and RP (Risk Participation) sheets. Headers on row 6.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                <input type="file" id="file-upload" className="hidden" accept=".xlsx,.xls,.xlsb" onChange={handleFileSelect} />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-medium mb-1">{selectedFile ? selectedFile.name : 'Click to select Excel file'}</p>
                  <p className="text-sm text-gray-500">Supported: .xlsx, .xls, .xlsb</p>
                </label>
                {selectedFile && <Badge className="mt-4 bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</Badge>}
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div><Label htmlFor="clear-existing" className="font-medium">Clear Existing Data</Label><p className="text-sm text-gray-500">Remove all existing loan book data before import</p></div>
                <Switch id="clear-existing" checked={clearExisting} onCheckedChange={setClearExisting} />
              </div>
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" size="lg" onClick={handleUpload} disabled={!selectedFile || uploading} data-testid="upload-btn">
                {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}</> : <><Upload className="h-4 w-4 mr-2" />Import Data</>}
              </Button>
              {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-amber-500 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Download className="h-5 w-5" />Download LB / RP Workbook</CardTitle>
              <CardDescription>Export the current Loan Book and Risk Participation tables as a .xlsx, in the same column layout as the original workbook.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full" onClick={handleExportLoanbookXlsx} disabled={exportingLoanbook} data-testid="export-loanbook-xlsx-btn">
                {exportingLoanbook ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Download className="h-4 w-4 mr-2" />Download LB &amp; RP as Excel</>}
              </Button>
            </CardContent>
          </Card>

          {importResult && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-green-600"><CheckCircle2 className="h-5 w-5" />Import Results</CardTitle></CardHeader>
              <CardContent>
                {/* Diagnostic: sheets detected in the file + which sheets the parser resolved.
                    Helps admin see IMMEDIATELY if a sheet was silently skipped due to naming. */}
                {(importResult.sheets_found?.length > 0 || importResult.sheets_resolved) && (
                  <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200 text-sm">
                    <p className="font-medium text-blue-800 mb-1">Sheets detected in file</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(importResult.sheets_found || []).map(s => (
                        <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                    {importResult.sheets_resolved && (
                      <div className="text-xs text-blue-700">
                        Resolved: {Object.entries(importResult.sheets_resolved)
                          .map(([target, actual]) => `${target}→${actual || '(missing)'}`)
                          .join(', ')}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Loan Book</p><p className="text-xl font-bold">{importResult.loan_book_imported?.toLocaleString()}</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Risk Participation</p><p className="text-xl font-bold">{importResult.risk_participation_imported?.toLocaleString()}</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Clients Created</p><p className="text-xl font-bold">{importResult.clients_created}</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Investors Found</p><p className="text-xl font-bold">{importResult.investors_identified}</p></div>
                </div>
                {(importResult.interest_lb_rows > 0 || importResult.interest_rp_rows > 0 || importResult.running_lb_rows > 0 || importResult.running_rp_rows > 0) && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {importResult.interest_lb_rows > 0 && <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-600">Interest-LB</p><p className="text-xl font-bold">{importResult.interest_lb_rows} rows</p></div>}
                    {importResult.interest_rp_rows > 0 && <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-600">Interest-RP</p><p className="text-xl font-bold">{importResult.interest_rp_rows} rows</p></div>}
                    {importResult.running_lb_rows > 0 && <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-600">Running-LB</p><p className="text-xl font-bold">{importResult.running_lb_rows} rows</p></div>}
                    {importResult.running_rp_rows > 0 && <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-600">Running-RP</p><p className="text-xl font-bold">{importResult.running_rp_rows} rows</p></div>}
                    {['Interest-LB2', 'Interest-RP2', 'Running-LB2', 'Running-RP2'].filter(t => importResult.timeseries_summary?.[t]).map(t => (
                      <div key={t} className="p-4 bg-blue-50 rounded-lg"><p className="text-sm text-blue-600">{t}</p><p className="text-xl font-bold">{importResult.timeseries_summary[t].loans} loans</p></div>
                    ))}
                  </div>
                )}
                {importResult.errors?.length > 0 && (
                  <div className="mt-4 p-4 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-800 flex items-center gap-2 mb-2"><AlertCircle className="h-4 w-4" />Warnings</p>
                    <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                      {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Preview + selective-approval flow for timeseries changes.
              Independent of the destructive Import Data flow above:
              admin can dry-run any Excel and pick which value-conflicts
              to accept, one-by-one, before writing to Mongo. */}
          <TimeseriesPreviewCard token={token} />

          {/* LoanBook Engine — shadow-mode replacement for the Running-LB/RP
              and Interest-LB/RP grids. Computes directly from LB/RP data every
              night at 00:01 SAST and only logs drift against what the Excel
              upload above already produced — never writes to anything the
              portal actually reads from. Safe to leave on. */}
          <Card data-testid="loanbook-engine-card">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5" />LoanBook Engine (Shadow Mode)</CardTitle>
                <CardDescription>
                  Computes balance &amp; interest directly from the LB/RP tables above, every night, and compares
                  the result against the Excel-uploaded Running-LB/RP and Interest-LB/RP figures. Observation only —
                  it does not change any figure the portal displays.
                </CardDescription>
              </div>
              <Link to="/admin/loanbook-engine/shadow">
                <Button variant="outline" size="sm" data-testid="view-shadow-comparison-btn">Compare Shadow Rows</Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="loanbook-engine-toggle" className="font-medium">Run nightly shadow calculation</Label>
                  <p className="text-sm text-gray-500">Off by default. Turning this on does not affect statements, reports, or dashboards.</p>
                </div>
                <Switch
                  id="loanbook-engine-toggle"
                  checked={!!engineStatus?.enabled}
                  disabled={engineLoading || engineToggling}
                  onCheckedChange={handleEngineToggle}
                  data-testid="loanbook-engine-switch"
                />
              </div>
              {engineStatus?.last_run && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Last Run</p><p className="text-sm font-medium">{engineStatus.last_run.run_date}</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Status</p><p className="text-sm font-medium capitalize">{engineStatus.last_run.status}</p></div>
                  <div className="p-4 bg-gray-50 rounded-lg"><p className="text-sm text-gray-500">Shadow Rows</p><p className="text-xl font-bold">{engineStatus.shadow_rows_total?.toLocaleString()}</p></div>
                  <div className={`p-4 rounded-lg ${engineStatus.shadow_rows_with_drift > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                    <p className={`text-sm ${engineStatus.shadow_rows_with_drift > 0 ? 'text-amber-700' : 'text-green-700'}`}>Rows With Drift</p>
                    <p className="text-xl font-bold">{engineStatus.shadow_rows_with_drift?.toLocaleString()}</p>
                  </div>
                </div>
              )}
              {!engineLoading && engineStatus?.enabled && !engineStatus?.last_run && (
                <p className="text-sm text-gray-500">Enabled — first shadow run happens at the next 00:01 SAST.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== INVESTOR PROFILES TAB ===== */}
        <TabsContent value="investors" className="space-y-4" data-testid="investor-upload-tab">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" />Upload Investor Profiles</CardTitle>
                <CardDescription>Create or update investor profiles. Matched on risk_participant_code (RP code). Only RP code is required.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full" onClick={() => downloadTemplate('investor')} data-testid="download-investor-template-btn">
                  <Download className="h-4 w-4 mr-2" />Download Template CSV
                </Button>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <Users className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <Input type="file" accept=".csv,.xlsx,.xls,.xlsb" onChange={e => { setInvestorFile(e.target.files?.[0]||null); setInvestorResult(null); }} className="max-w-xs mx-auto" data-testid="investor-file-input" />
                  {investorFile && <p className="text-sm text-gray-600 mt-2">{investorFile.name}</p>}
                </div>
                <Button onClick={() => handleInvestorUpload(false)} disabled={investorUploading || !investorFile} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="investor-upload-btn">
                  {investorUploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : <><Upload className="h-4 w-4 mr-2" />Upload Investor Profiles</>}
                </Button>
                {investorResult && <UploadResult result={investorResult} type="investor" />}
                {/* Conflict detection and merge confirmation */}
                {investorResult?.conflicts?.length > 0 && investorResult.mode === 'preview' && (
                  <div className="p-4 bg-orange-50 border border-orange-300 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-orange-800">
                      {investorResult.conflict_count} Conflict{investorResult.conflict_count !== 1 ? 's' : ''} Detected
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {investorResult.conflicts.map((c, i) => (
                        <div key={i} className="p-2 bg-white rounded border border-orange-200 text-xs">
                          <p className="font-medium text-orange-900">Row {c.row}: {c.rp_code} — {c.existing_name || 'Unnamed'}</p>
                          {c.fields?.map((f, j) => (
                            <p key={j} className="text-orange-700 ml-2">
                              {f.field}: <span className="line-through">{f.existing}</span> → <span className="font-medium">{f.new}</span>
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                    <Button
                      onClick={handleConfirmMerge}
                      disabled={investorUploading}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="confirm-merge-btn"
                    >
                      {investorUploading
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
                        : <>Confirm Merge & Overwrite ({investorResult.conflict_count} conflict{investorResult.conflict_count !== 1 ? 's' : ''})</>
                      }
                    </Button>
                  </div>
                )}
                {investorResult?.created_count > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          {investorResult.created_count} new investor account{investorResult.created_count !== 1 ? 's' : ''} created
                        </p>
                        <p className="text-xs text-blue-600 mt-0.5">
                          Please review the data above, then send welcome emails with password-set links.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSendWelcomeEmails}
                      disabled={sendingEmails || emailResult?.sent_count > 0}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="send-welcome-emails-btn"
                    >
                      {sendingEmails
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                        : emailResult?.sent_count > 0
                          ? <><CheckCircle className="h-4 w-4 mr-2" />{emailResult.sent_count} Welcome Email{emailResult.sent_count !== 1 ? 's' : ''} Sent</>
                          : <><FileText className="h-4 w-4 mr-2" />Send Welcome Emails ({investorResult.created_count})</>}
                    </Button>
                    {emailResult && (
                      <div className="text-xs space-y-1">
                        {emailResult.errors?.map((e, i) => (
                          <p key={i} className="text-red-600">{e.error}</p>
                        ))}
                        {emailResult.sent_count === 0 && !emailResult.errors?.length && (
                          <p className="text-amber-700">{emailResult.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">CSV Column Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-xs">
                  {[
                    ['risk_participant_code', 'REQUIRED — Primary identifier (e.g. 01-01). Format column as Text in Excel to prevent date conversion.'],
                    ['prefix', 'Title: Mr, Mrs, Ms, Dr, Prof'],
                    ['first_name', 'Optional — can be added later'],
                    ['last_name', 'Optional — can be added later'],
                    ['full_name', 'Optional — derived from prefix + first + last if blank'],
                    ['email', 'Optional — investor login email. If blank, managed via admin_email'],
                    ['phone', 'Contact number'],
                    ['entity_name', 'Trust / company name'],
                    ['entity_type', 'Individual / Company / Trust / Partnership'],
                    ['id_number', 'SA ID or passport number'],
                    ['tax_number', 'SARS tax number'],
                    ['physical_address', 'Full physical address'],
                    ['postal_address', 'Postal address'],
                    ['bank_name', 'Bank name'],
                    ['bank_branch_code', '6-digit branch code'],
                    ['bank_account_number', 'Account number'],
                    ['bank_account_type', 'Cheque / Savings / Current'],
                    ['investec_account_number', 'Investec bank account number'],
                    ['investor_type', 'Individual / Institutional / Trust / Corporate'],
                    ['investment_exclusions', 'Semicolon-separated excluded sectors: Cannabis;Gambling;Mining'],
                    ['investment_limits', 'Format: {factor;%limit}. Factors: single_client, sector, commodity, stage, tranche, single_loan. E.g. {single_client;25%};{sector;40%}'],
                    ['admin_email', 'Admin who manages this profile. Enables multi-profile login.'],
                    ['additional_emails', 'Semicolon-separated additional access emails'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-44 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                    Only risk_participant_code is required. All other fields are optional and can be updated later via re-upload or the investor detail page. Investors without email are managed through admin login.
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== CLIENT PROFILES TAB ===== */}
        <TabsContent value="clients" className="space-y-4" data-testid="client-upload-tab">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" />Upload Client Profiles</CardTitle>
                <CardDescription>Create or update client company profiles with multiple contacts and sector classification. Matched on client_id.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button variant="outline" className="w-full" onClick={() => downloadTemplate('client')} data-testid="download-client-template-btn">
                  <Download className="h-4 w-4 mr-2" />Download Template CSV
                </Button>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                  <Building2 className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <Input type="file" accept=".csv,.xlsx,.xls,.xlsb" onChange={e => { setClientFile(e.target.files?.[0]||null); setClientResult(null); }} className="max-w-xs mx-auto" data-testid="client-file-input" />
                  {clientFile && <p className="text-sm text-gray-600 mt-2">{clientFile.name}</p>}
                </div>
                <Button onClick={handleClientUpload} disabled={clientUploading || !clientFile} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="client-upload-btn">
                  {clientUploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : <><Upload className="h-4 w-4 mr-2" />Upload Client Profiles</>}
                </Button>
                {clientResult && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2 text-sm">
                    <p className="font-medium text-blue-800">{clientResult.message}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-2 bg-green-100 rounded">
                        <p className="text-green-700 font-bold text-lg">{clientResult.created_count}</p>
                        <p className="text-green-600 text-xs">Created</p>
                      </div>
                      <div className="text-center p-2 bg-amber-100 rounded">
                        <p className="text-amber-700 font-bold text-lg">{clientResult.updated_count}</p>
                        <p className="text-amber-600 text-xs">Updated</p>
                      </div>
                      <div className="text-center p-2 bg-blue-100 rounded">
                        <p className="text-blue-700 font-bold text-lg">{clientResult.contacts_created_count || 0}</p>
                        <p className="text-blue-600 text-xs">Contacts</p>
                      </div>
                      <div className="text-center p-2 bg-red-100 rounded">
                        <p className="text-red-700 font-bold text-lg">{clientResult.error_count}</p>
                        <p className="text-red-600 text-xs">Errors</p>
                      </div>
                    </div>
                    {clientResult.contacts_created?.length > 0 && (
                      <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 max-h-32 overflow-y-auto">
                        <p className="text-xs font-medium text-blue-800 mb-1">Contact accounts created:</p>
                        {clientResult.contacts_created.map((c, i) => (
                          <p key={i} className="text-blue-700 text-xs">{c}</p>
                        ))}
                      </div>
                    )}
                    {clientResult.errors?.length > 0 && (
                      <div className="mt-2 p-3 bg-red-50 rounded border border-red-200 max-h-40 overflow-y-auto">
                        {clientResult.errors.map((e, i) => (
                          <p key={i} className="text-red-700 text-xs">Row {e.row}: {e.error}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">CSV Column Reference (27 columns)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5 text-xs">
                  <p className="font-medium text-gray-700 mb-2 border-b pb-1">Client Identification</p>
                  {[
                    ['client_id', 'Required — must match loan book ClientID'],
                    ['company_name', 'Required — legal entity name'],
                    ['trading_name', 'Trading-as name if different'],
                    ['registration_number', 'CIPC registration number'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <p className="font-medium text-gray-700 mt-3 mb-2 border-b pb-1">Contacts (up to 3, each gets a login)</p>
                  {[
                    ['contact1_name', 'Primary contact full name'],
                    ['contact1_email', 'Primary contact email (login)'],
                    ['contact1_phone', 'Primary contact phone'],
                    ['contact1_role', 'Role: Director, CFO, Manager, etc.'],
                    ['contact2_name', 'Second contact name'],
                    ['contact2_email', 'Second contact email (login)'],
                    ['contact2_phone', 'Second contact phone'],
                    ['contact2_role', 'Second contact role'],
                    ['contact3_name', 'Third contact name'],
                    ['contact3_email', 'Third contact email (login)'],
                    ['contact3_phone', 'Third contact phone'],
                    ['contact3_role', 'Third contact role'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <p className="font-medium text-gray-700 mt-3 mb-2 border-b pb-1">Address & Tax</p>
                  {[
                    ['physical_address', 'Business physical address'],
                    ['postal_address', 'Postal address'],
                    ['city', 'City'],
                    ['province', 'Province / state'],
                    ['postal_code', 'Postal / ZIP code'],
                    ['vat_number', 'VAT registration number'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <p className="font-medium text-gray-700 mt-3 mb-2 border-b pb-1">Bank Details</p>
                  {[
                    ['bank_name', 'Client bank name (e.g. FNB, Investec)'],
                    ['bank_branch', 'Branch name'],
                    ['bank_branch_code', 'Branch code'],
                    ['bank_account_number', 'Account number'],
                    ['bank_swift_code', 'SWIFT/BIC code'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <p className="font-medium text-gray-700 mt-3 mb-2 border-b pb-1">Statement Recipients</p>
                  {[
                    ['statement_recipients', 'Comma-separated emails beyond contacts (auto-deduped)'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <p className="font-medium text-gray-700 mt-3 mb-2 border-b pb-1">Sector Classification (5-level hierarchy)</p>
                  {[
                    ['product_sector', 'Agriculture, Resources, Industrial, Consumer, Logistics, Financial, Retail'],
                    ['product_sub_sector', 'e.g. Crops, Minerals, Manufacturing, Oil_and_Gas'],
                    ['category', 'e.g. Cereals, Chrome, Coal, Flooring'],
                    ['product', 'e.g. Maize, Ore, Thermal Coal, PLYWOOD'],
                    ['grade', 'e.g. 42/40, RB1, Anthracite'],
                  ].map(([col, desc]) => (
                    <div key={col} className="flex gap-2">
                      <span className="font-mono text-blue-700 w-40 shrink-0">{col}</span>
                      <span className="text-gray-500">{desc}</span>
                    </div>
                  ))}

                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                    Only client_id and company_name are required. Each contact with an email gets a client portal login. Bank details are stored for future drawdown requests. Statement recipients receive statements alongside contacts (duplicates auto-removed).
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="loan-cashflows" className="space-y-4" data-testid="loan-cashflows-tab">
          <LoanCashflowUploadCard token={token} />
          <FirstDrawReviewCard token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDataImportPage;

